/**
 * Close a Repancy desktop window.
 *
 * Companion to openWindow. Uses the same NIPE_KNOWN_WINDOWS catalogue
 * so the model can't close paths the desktop doesn't know about.
 */

import type { NipeTool } from './types';
import { NIPE_KNOWN_WINDOWS } from './openWindow';

export interface CloseWindowContext {
  /** Wired from useNipeChat → WindowContext.closeWindow. Missing in
   * headless fixtures; handler surfaces a friendly error in that case. */
  closeWindow?: (path: string) => Promise<{ closed: boolean }>;
}

const KNOWN_PATHS = NIPE_KNOWN_WINDOWS.map((w) => w.path);

export const closeWindowTool: NipeTool<CloseWindowContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'closeWindow',
      description:
        'Close a Repancy desktop window. Use when the user asks to close, ' +
        'dismiss, or exit a window. Known paths: ' +
        KNOWN_PATHS.join(', '),
      parameters: {
        type: 'object',
        properties: {
          path: {
            type: 'string',
            description: 'Window path. One of: ' + KNOWN_PATHS.join(', '),
          },
        },
        required: ['path'],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: false,
  handler: async (args, ctx) => {
    if (!ctx.closeWindow) {
      return { error: 'closeWindow integration is not available in this session.' };
    }
    const path = typeof args.path === 'string' ? args.path : '';
    if (!KNOWN_PATHS.includes(path)) {
      return {
        error: `Unknown window path "${path}". Known: ${KNOWN_PATHS.join(', ')}`,
      };
    }
    return await ctx.closeWindow(path);
  },
};
